// Step order, labels and icons for the client-facing checkup survey (#598).
// The stepper, the pages, the review step's jump links AND the logged-in
// client's top nav (store/index.js's surveyRoutes) all read this — nothing
// else defines checkup step order.
//
// `icon` is a FontAwesome name registered in nuxt.config.js's SOLID_ICONS; it
// is used by the nav, not by the stepper, which numbers steps instead.
//
// The generic helpers moved to data/wizard.js in #656 so /renewal/* could run
// on the same shell; the three exports below are unchanged in name, shape and
// behaviour, and remain the way everything outside this file reaches them.
import { makeWizard } from '~/data/wizard'

export const STEPS = [
    { id: 'welcome', path: '/checkup/welcome', label: 'Welcome', icon: 'play' },
    { id: 'about', path: '/checkup/about', label: 'About you', icon: 'address-card' },
    { id: 'income', path: '/checkup/income', label: 'Income', icon: 'sack-dollar' },
    // Doctors + hospitals share one page since #656 (comment 2 asked for the
    // merge in BOTH wizards); /checkup/hospitals redirects here.
    { id: 'doctors', path: '/checkup/doctors', label: 'Doctors & hospitals', icon: 'user-md' },
    { id: 'medications', path: '/checkup/medications', label: 'Medications', icon: 'prescription-bottle-alt' },
    { id: 'pharmacy', path: '/checkup/pharmacy', label: 'Pharmacy', icon: 'clinic-medical' },
    { id: 'sign', path: '/checkup/sign', label: 'Sign', icon: 'pen' },

    // Selfserve only, mirroring how the classic survey nav gated it
    // (store/index.js's surveyRoutes: `isSelfserve ? plans : null`). Fullserve
    // clients get a recommendation from an advisor instead of shopping the
    // list themselves, and /checkup has been fullserve-only until now, so
    // without this predicate every existing checkup client would suddenly
    // gain a step.
    // Deliberately AFTER `sign`, and the last step a selfserve client walks:
    // the Scope of Appointment is signed before any specific plan is shown,
    // and shopping the list is a more useful place to end than a confirmation
    // screen about a consultation they are not having.

    // hiding this plans page from everyone for now
    // { id: 'plans', path: '/checkup/plans', label: 'Plans', icon: 'list-ol', when: g => g.selfserve },
    // Un-hiding it: restore `when: g => !g.selfserve` on `done` below in the
    // same change, or a selfserve client walks plans AND done. sign.vue sends
    // a selfserve client to plans whenever the step exists, so it needs nothing.

    // `nav: false` — reachable, but not offered as a jump. The confirmation
    // screen reads as terminal ("your advisor has everything"), and the nav is
    // the only way to land on it out of order: the stepper refuses forward
    // jumps, and sign->done goes through completeAndGo(). The old nav did offer
    // it (as "Complete", /survey/completion) and that page called
    // completeSurvey() on mount, so clicking it mid-survey actually marked the
    // client done — and re-pinged #preconsults-surveys-queue on every visit,
    // since survey_completed_by_client_or_agent is not idempotent.
    //
    // Both tiers end here while Plans is hidden. Its fullserve copy is all
    // about the consultation ("your advisor will come fully prepared",
    // "schedule your consultation"), which is wrong for a selfserve client who
    // is not getting a call, so pages/checkup/done.vue renders DoneStep's
    // selfserve mode for them. With Plans back, selfserve ends on `plans` and
    // this is fullserve-only again (`when: g => !g.selfserve`).
    { id: 'done', path: '/checkup/done', label: 'Done', icon: 'flag-checkered', nav: false },
]

export const CHECKUP_WIZARD = makeWizard({
    id: 'checkup',
    basePath: '/checkup',
    steps: STEPS,
})

export const { stepIndex, stepForRoute } = CHECKUP_WIZARD
